import { NavLink, useNavigate } from 'react-router-dom';
import { User } from '../models/User';
import useUserAccount from '../../hooks/stateHooks/userAccountHook';
import { ChevronDown, LogOut, Menu, Moon, PanelLeftClose, PanelLeftOpen, ShieldCheck, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getScreenMode, SET_SCREEN_MODE } from '../../states/slices/globalSlice';
import { SCREEN_MODES } from '../../resources/enums';
import Avatar from '../elements/Avatar';

interface INavbarProps {
  handleMenuClick?: (e: any) => void;
  isSticky?: boolean;
  hideProfileSection?: boolean;
  onSetMobileOpen?: () => void;
  onSetCollapsed?: () => void;
  collapsed?: boolean;
}

function Navbar(props: INavbarProps) {
  const { signOut, nameInitials, getProfile } = useUserAccount();
  const user = new User(getProfile());
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const darkMode = useSelector(getScreenMode) === SCREEN_MODES.DARK;

  const dispatch = useDispatch();

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode)
  }, [darkMode])

  const accountActions = [
    {
      label: 'Profile',
      callback: () => {
        navigate(`/admin/settings?tab=profile`);
      }
    },
    {
      label: 'Logout',
      callback: () => {
        signOut();
      }
    }
  ];

  function toggleDarkMode() {
    dispatch(SET_SCREEN_MODE(darkMode ? SCREEN_MODES.LIGHT : SCREEN_MODES.DARK));
  }

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/90 px-4 backdrop-blur sm:px-6 dark:border-slate-800 dark:bg-slate-900/90">
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => props.onSetMobileOpen?.()} className="rounded-lg p-2 hover:bg-slate-100 lg:hidden dark:hover:bg-slate-800">
          <Menu className="h-5 w-5" />
        </button>

        <button type="button" onClick={() => props.onSetCollapsed?.()} className="hidden rounded-lg p-2 hover:bg-slate-100 lg:block dark:hover:bg-slate-800">
          {props.collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
        </button>

        {/* <div className="relative hidden md:block">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input
            placeholder="Search anything..."
            className="w-64 rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm outline-none focus:border-slate-400 dark:border-slate-800 dark:bg-slate-800"
          />
        </div> */}
      </div>

      <div className="flex items-center gap-2">
        {/* <CompanySwitcher /> */}

        <button type="button" onClick={toggleDarkMode} className="rounded-xl p-2.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
          {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>

        {/* <button type="button" className="relative rounded-xl p-2.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
          <Bell className="h-5 w-5" />
          <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-red-500" />
        </button> */}

        <div className="relative">
          <button type="button" onClick={() => setProfileOpen((value) => !value)} className="flex items-center gap-2 rounded-xl p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800">
            <Avatar
              nameInitials={nameInitials}
              src={user.profileImage}
              size='xs'
            />
            <ChevronDown className="hidden h-4 w-4 text-slate-400 sm:block" />
          </button>

          {profileOpen && (
            <div className="absolute right-0 z-50 mt-2 w-64 rounded-xl border border-slate-200 bg-white p-2 shadow-xl dark:border-slate-800 dark:bg-slate-900">
              <div className="border-b border-slate-100 px-3 py-2.5 dark:border-slate-800">
                <p className="font-semibold">
                  {user ? `${user.firstName} ${user.lastName}` : "User"}
                </p>
                <p className="truncate text-xs text-slate-500 dark:text-text-dark-secondary">{user?.email}</p>
              </div>

              {/* <Can permission="manage_users"> */}
              <NavLink
                to="/admin/settings"
                onClick={() => setProfileOpen(false)}
                className="mt-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <ShieldCheck className="h-4 w-4" />
                Users & permissions
              </NavLink>
              {/* </Can> */}

              <button
                type="button"
                onClick={() => void signOut()}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
    // <div className={clsx(
    //   "py-3 bg-white max-w-screen w-full",
    //   { ["fixed top-0 z-40"]: isSticky }
    // )}>
    //   <div className="flex items-center m-auto mx-5 sm:mx-10">
    //     <div className="flex items-center w-full justify-between">
    //       <div className="flex items-center mr-5 sm:mr-10">
    //         <div className="lg:hidden mr-2">
    //           <div onClick={props.handleMenuClick} className="w-8 items-center">
    //             <Menu />
    //           </div>
    //         </div>

    //         {/* NavBrand */}
    //         <Link to={DASHBOARD}>
    //           <span className="border-none flex items-center">
    //             <div>
    //               {user.company?.logo ? (
    //                 <div className='w-[50px] h-[50px]'>
    //                   <img className="w-full h-full" />
    //                 </div>

    //               ) : (
    //                 <div className='flex items-center'>
    //                   <div className='bg-gray-200 rounded-[50%] mr-2 w-[30px] h-[30px] md:w-[50px] md:h-[50px] flex items-center justify-center border'>
    //                     <GoOrganization className='w-full h-full p-1 md:p-2' />
    //                   </div>
    //                   <h3 className='text-black font-bold w-[187px] md:w-[250px] text-md md:text-xl text-ellipsis whitespace-nowrap overflow-hidden ...'>{user.company?.name}</h3>
    //                 </div>

    //               )}
    //             </div>
    //           </span>
    //         </Link>
    //       </div>

    //       {!props.hideProfileSection && (
    //         <div className="flex items-center gap-4">
    //           {/* <NotificationIcon className="cursor-pointer" /> */}
    //           <div className="">
    //             <PopOnMe actions={accountActions}>
    //               <div className="cursor-pointer flex items-center">
    //                 <Avatar src={user.profileImage} size="sm" />
    //               </div>
    //             </PopOnMe>
    //           </div>
    //         </div>
    //       )}
    //     </div>
    //   </div>
    // </div>
  );
}

export default Navbar;
